// Active deals by stage, phone layout: the stage sections stacked, each with its heading and count; empty
// stages say "None". Also the one-line notice shown over the sections when no loan is in view.
import type { ReactNode } from "react";
import type { StageSection } from "../lib/stageGroups";
import { stageTone } from "../lib/stages";
import { useIsDark } from "../lib/prefs";

export function StageSections<T>({ sections, renderItems }: { sections: StageSection<T>[]; renderItems: (items: T[]) => ReactNode }) {
  const dark = useIsDark();
  return (
    <div>
      {sections.map((s) => {
        const swatch = s.stage ? stageTone(s.stage, dark)?.solid : undefined;
        return (
          <section key={s.key} id={`stage-section-${s.key}`} aria-labelledby={`stage-heading-${s.key}`} className="scroll-mt-[108px]">
            <h2 id={`stage-heading-${s.key}`} className="flex items-center gap-2 border-b border-subtle bg-sunken px-4 py-2 text-[12px] font-bold tracking-wide text-ink uppercase">
              <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: swatch ?? "transparent", boxShadow: swatch ? undefined : "inset 0 0 0 1px var(--border-default)" }} aria-hidden />
              <span className="min-w-0 flex-1 truncate">{s.stage ? `${s.stage.sort}. ${s.label}` : s.label}</span>
              <span className="tnum text-ink-2">{s.items.length}</span>
            </h2>
            {s.items.length ? renderItems(s.items) : <p className="border-b border-subtle px-4 py-2 text-[13px] text-ink-3">None</p>}
          </section>
        );
      })}
    </div>
  );
}

/** Shown above the stage sections when the view is empty, so the (all "None") funnel stays visible. */
export function EmptyViewNotice({ noLoans, onClearFilters }: { noLoans: boolean; onClearFilters: () => void }) {
  return (
    <p role="status" className="text-[13px] text-ink-2">
      {noLoans ? (
        <>
          <b className="text-ink">No loans yet.</b> Press <b>New loan</b> to add one, then paste rows straight from Excel with ⌘V.
        </>
      ) : (
        <>
          <b className="text-ink">No loans match these filters.</b>{" "}
          <button className="focus-ring text-link underline" onClick={onClearFilters}>
            Clear filters
          </button>
        </>
      )}
    </p>
  );
}
